import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import { useAuthStore } from '@starling/core/auth/store';
import type { components } from '@/api/types.gen';

// «Моя организация» — платформенные эндпоинты `/api/v1/organizations/*`
// (секция в openapi-vision.yaml, накопитель V1 №4). Достаточно любой сессии
// (account-only или tenant) — бэкенд читает только claims.sub.

export type MyOrganization = components['schemas']['MyOrganization'];
export type MyOrganizationsResponse = components['schemas']['MyOrganizationsResponse'];
export type OrganizationCreate = components['schemas']['OrganizationCreate'];
export type OrganizationContactsUpdate = components['schemas']['OrganizationContactsUpdate'];
export type TelegramLinkResponse = components['schemas']['TelegramLinkResponse'];
export type VerificationAttachRequest = components['schemas']['VerificationAttachRequest'];
export type VerificationConfirmRequest = components['schemas']['VerificationConfirmRequest'];
export type VerificationConfirmResponse = components['schemas']['VerificationConfirmResponse'];

export const MY_ORGANIZATIONS_KEY = ['organizations', 'my'] as const;

/** Организации текущего пользователя со статусом верификации.
 *
 * `pollIntervalMs` — периодический опрос (пока ждём подтверждения Telegram
 * от бота: оно приходит вебхуком мимо браузера, как статус сообщения в Light);
 * несколько наблюдателей одного ключа — React Query берёт наименьший интервал.
 */
export function useMyOrganizationsQuery(options: { pollIntervalMs?: number | false } = {}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  return useQuery({
    queryKey: MY_ORGANIZATIONS_KEY,
    queryFn: () => api.get<MyOrganizationsResponse>('/api/v1/organizations/my'),
    enabled: isAuthenticated,
    refetchInterval: options.pollIntervalMs ?? false,
    refetchIntervalInBackground: false,
  });
}

/** Создать организацию (неверифицированную; вызывающий — owner). */
export function useCreateOrganizationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['organizations', 'create'],
    mutationFn: (input: OrganizationCreate) =>
      api.post<MyOrganization>('/api/v1/organizations', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_ORGANIZATIONS_KEY });
    },
  });
}

/**
 * Контакты организации (доводка V1 №16): телефон/Telegram — реквизит
 * организации, наследуется формой объявления; без хотя бы одного публикация
 * закрыта. Пустая строка — очистить. 422 `invalid_phone` / `invalid_telegram`.
 */
export function useUpdateOrganizationContactsMutation(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['organizations', 'contacts', organizationId],
    mutationFn: (input: OrganizationContactsUpdate) =>
      api.patch<MyOrganization>(`/api/v1/organizations/${organizationId}`, input),
    onSuccess: (updated) => {
      qc.setQueryData<MyOrganizationsResponse>(MY_ORGANIZATIONS_KEY, (prev) =>
        prev ? { items: prev.items.map((org) => (org.id === updated.id ? updated : org)) } : prev,
      );
    },
  });
}

function useReplaceOrganization() {
  const qc = useQueryClient();
  return (updated: MyOrganization): void => {
    qc.setQueryData<MyOrganizationsResponse>(MY_ORGANIZATIONS_KEY, (prev) =>
      prev ? { items: prev.items.map((org) => (org.id === updated.id ? updated : org)) } : prev,
    );
  };
}

/** №16-C: SMS-код на сохранённый телефон организации (всегда 200; 422 `phone_missing`). */
export function useRequestPhoneCodeMutation(organizationId: string) {
  return useMutation({
    mutationKey: ['organizations', 'contacts', 'phone', 'request', organizationId],
    mutationFn: () =>
      api.post<{ status: string }>(
        `/api/v1/organizations/${organizationId}/contacts/phone/request-code`,
      ),
  });
}

/** №16-C: подтвердить телефон кодом (400 `contact_code_invalid` — единый). */
export function useConfirmPhoneCodeMutation(organizationId: string) {
  const replace = useReplaceOrganization();
  return useMutation({
    mutationKey: ['organizations', 'contacts', 'phone', 'confirm', organizationId],
    mutationFn: (input: { code: string }) =>
      api.post<MyOrganization>(
        `/api/v1/organizations/${organizationId}/contacts/phone/confirm`,
        input,
      ),
    onSuccess: replace,
  });
}

/** №16-C: deep-link бота для подтверждения Telegram (422 `telegram_missing`). */
export function useTelegramLinkMutation(organizationId: string) {
  return useMutation({
    mutationKey: ['organizations', 'contacts', 'telegram', 'link', organizationId],
    mutationFn: () =>
      api.post<TelegramLinkResponse>(
        `/api/v1/organizations/${organizationId}/contacts/telegram/link`,
      ),
  });
}

/**
 * Прикрепить карточку 2ГИС и запросить код (SMS/email).
 * Ответ ВСЕГДА 200 при валидном вводе (не раскрываем, ушёл ли код).
 */
export function useVerifyAttachMutation(organizationId: string) {
  return useMutation({
    mutationKey: ['organizations', 'verify', 'attach', organizationId],
    mutationFn: (input: VerificationAttachRequest) =>
      api.post<{ status: string }>(`/api/v1/organizations/${organizationId}/verify/attach`, input),
  });
}

/**
 * Подтвердить организацию одноразовым кодом. 400 `verification_code_invalid` —
 * единый. Инвалидации здесь НЕТ намеренно: рефетч сразу размонтировал бы флоу
 * (карточка стала бы verified) и финальный экран ✓ из PostFlow не показался бы.
 * Список перечитывает кнопка «Готово» финального шага (VerificationFlow).
 */
export function useVerifyConfirmMutation(organizationId: string) {
  return useMutation({
    mutationKey: ['organizations', 'verify', 'confirm', organizationId],
    mutationFn: (input: VerificationConfirmRequest) =>
      api.post<VerificationConfirmResponse>(
        `/api/v1/organizations/${organizationId}/verify/confirm`,
        input,
      ),
  });
}
